import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Trash2 } from 'lucide-react';

// Only enables swipe on touch devices (tablet/phone)
// Requires deliberate horizontal swipe - won't trigger on taps or vertical scrolls
const SwipeToDelete = ({ children, onDelete, disabled = false }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(0);
  const background = useTransform(x, [-100, 0], ['#ef4444', 'transparent']);
  const deleteOpacity = useTransform(x, [-100, -50, 0], [1, 0.5, 0]);
  const startPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  
  // Check if device supports touch
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  
  if (!isTouchDevice || disabled) {
    return <>{children}</>;
  }
  
  const handleDragStart = (event, info) => {
    startPos.current = { x: info.point.x, y: info.point.y };
    hasMoved.current = false;
  };
  
  const handleDrag = (event, info) => {
    const dx = Math.abs(info.point.x - startPos.current.x);
    const dy = Math.abs(info.point.y - startPos.current.y);
    
    // Only allow horizontal drag if movement is predominantly horizontal
    // This prevents accidental swipes during vertical scrolling
    if (!hasMoved.current && dx > 10) {
      if (dx > dy * 2) {
        hasMoved.current = true;
        setIsDragging(true);
      }
    }
    
    // If not dragging horizontally, reset x position
    if (!hasMoved.current) {
      x.set(0);
    }
  };
  
  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    
    // Only trigger delete if we were actually dragging horizontally
    if (hasMoved.current && info.offset.x < -80) {
      setIsDeleting(true);
      onDelete?.();
    }
    
    hasMoved.current = false;
  };
  
  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Delete background */}
      <motion.div 
        className="absolute inset-0 flex items-center justify-end pr-6 rounded-xl"
        style={{ background }}
      >
        <motion.div style={{ opacity: deleteOpacity }}>
          <Trash2 className="w-6 h-6 text-white" />
        </motion.div>
      </motion.div>
      
      {/* Swipeable content */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -100, right: 0 }}
        dragElastic={0.05}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ x }}
        className="relative bg-card rounded-xl touch-pan-y"
      >
        {children}
      </motion.div>
    </div>
  );
};

export default SwipeToDelete;
